'use client';

import React, { useState } from 'react';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Legend, LineChart, Line, XAxis, YAxis, CartesianGrid, BarChart, Bar } from 'recharts';
import { TrendingUp, TrendingDown, Minus, ShieldCheck, AlertTriangle, CheckCircle2, Clock, Download, ArrowLeft } from 'lucide-react';
import { useRole } from '@/contexts/RoleContext';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';



// ─── Mock Data ────────────────────────────────────────────────────────────────

const COMPLIANCE_STATUS_DATA = [
  { name: 'Compliant', value: 38, color: '#0d9488' },
  { name: 'Minor Issues', value: 14, color: '#d97706' },
  { name: 'Non-Compliant', value: 7, color: '#dc2626' },
  { name: 'Under Review', value: 9, color: '#0284c7' },
  { name: 'Exempt', value: 4, color: '#6b7280' },
];

const RISK_TREND_DATA = [
  { month: 'Oct', critical: 4, high: 11, moderate: 18, low: 22 },
  { month: 'Nov', critical: 5, high: 13, moderate: 16, low: 24 },
  { month: 'Dec', critical: 6, high: 12, moderate: 19, low: 21 },
  { month: 'Jan', critical: 8, high: 15, moderate: 17, low: 20 },
  { month: 'Feb', critical: 7, high: 14, moderate: 20, low: 23 },
  { month: 'Mar', critical: 5, high: 11, moderate: 22, low: 26 },
];

const REMEDIATION_FUNNEL_DATA = [
  { name: 'Issues Identified', value: 87, fill: '#dc2626', issueStatus: '' },
  { name: 'Under Review', value: 64, fill: '#f97316', issueStatus: 'Under Review' },
  { name: 'Remediation In Progress', value: 41, fill: '#d97706', issueStatus: 'Remediation' },
  { name: 'Pending Validation', value: 22, fill: '#0284c7', issueStatus: 'Pending Validation' },
  { name: 'Closed / Resolved', value: 15, fill: '#0d9488', issueStatus: 'Closed' },
];

const VENDOR_RISK_HEATMAP = [
  { dept: 'IT Infra', critical: 3, high: 5, moderate: 4, low: 2 },
  { dept: 'IT Security', critical: 2, high: 4, moderate: 3, low: 1 },
  { dept: 'Finance', critical: 1, high: 2, moderate: 5, low: 4 },
  { dept: 'City Planning', critical: 0, high: 3, moderate: 6, low: 5 },
  { dept: 'HR', critical: 1, high: 1, moderate: 3, low: 6 },
  { dept: 'Legal', critical: 0, high: 2, moderate: 2, low: 3 },
  { dept: 'Public Works', critical: 2, high: 3, moderate: 4, low: 3 },
];

const SLA_METRICS = [
  { vendor: 'TechCore Solutions', metric: 'System Uptime', target: '99.9%', current: '99.95%', status: 'Met', trend: 'up' },
  { vendor: 'SecureNet Defense', metric: 'Incident Response', target: '< 15 min', current: '12 min', status: 'Met', trend: 'up' },
  { vendor: 'DataBridge Analytics', metric: 'API Response Time', target: '< 200ms', current: '340ms', status: 'Breached', trend: 'down' },
  { vendor: 'CivicHR Systems', metric: 'Ticket Resolution', target: '< 4 hrs', current: '5.2 hrs', status: 'At Risk', trend: 'down' },
  { vendor: 'PayrollPro Inc.', metric: 'Processing Accuracy', target: '99.5%', current: '99.7%', status: 'Met', trend: 'stable' },
  { vendor: 'CloudVault Storage', metric: 'Recovery Time', target: '< 2 hrs', current: '1.8 hrs', status: 'Met', trend: 'up' },
  { vendor: 'NetGuard Systems', metric: 'False Positive Rate', target: '< 5%', current: '7.1%', status: 'Breached', trend: 'down' },
  { vendor: 'PermitFlow Gov', metric: 'Platform Availability', target: '99.5%', current: '98.9%', status: 'At Risk', trend: 'down' },
];

const SLA_SUMMARY = [
  { month: 'Oct', met: 82, atRisk: 11, breached: 7 },
  { month: 'Nov', met: 78, atRisk: 14, breached: 8 },
  { month: 'Dec', met: 80, atRisk: 12, breached: 8 },
  { month: 'Jan', met: 75, atRisk: 16, breached: 9 },
  { month: 'Feb', met: 77, atRisk: 14, breached: 9 },
  { month: 'Mar', met: 81, atRisk: 12, breached: 7 },
];

// ─── Tooltip ──────────────────────────────────────────────────────────────────

function TooltipCard({ active, payload, label }: { active?: boolean; payload?: Array<{ name: string; value: number; color: string }>; label?: string }) {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="card p-2.5 shadow-md text-xs min-w-[130px]">
      {label && <p className="font-semibold text-[hsl(var(--text-primary))] mb-1.5">{label}</p>}
      {payload.map((p, i) => (
        <div key={i} className="flex items-center justify-between gap-3">
          <span className="flex items-center gap-1 text-[hsl(var(--text-muted))]">
            <span className="w-2 h-2 rounded-full inline-block" style={{ background: p.color }} />
            {p.name}
          </span>
          <span className="font-medium text-[hsl(var(--text-primary))] tabular-nums">{p.value}</span>
        </div>
      ))}
    </div>
  );
}

// ─── KPI Card ─────────────────────────────────────────────────────────────────

function KPICard({ label, value, sub, color, icon: Icon }: { label: string; value: string | number; sub: string; color: string; icon: React.ElementType }) {
  return (
    <div className="card p-4 flex items-start gap-3">
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${color}`}>
        <Icon size={16} className="text-white" />
      </div>
      <div className="min-w-0">
        <p className="text-xs text-[hsl(var(--text-muted))] truncate">{label}</p>
        <p className="text-xl font-bold text-[hsl(var(--text-primary))] leading-tight">{value}</p>
        <p className="text-2xs text-[hsl(var(--text-muted))] mt-0.5">{sub}</p>
      </div>
    </div>
  );
}

// ─── SLA Status Badge ─────────────────────────────────────────────────────────

function SLABadge({ status }: { status: string }) {
  const styles: Record<string, string> = {
    Met: 'bg-teal-100 text-teal-700 dark:bg-teal-950 dark:text-teal-400',
    'At Risk': 'bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-400',
    Breached: 'bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-400',
  };
  return (
    <span className={`text-2xs font-semibold px-2 py-0.5 rounded-full ${styles[status] ?? 'bg-gray-100 text-gray-600'}`}>
      {status}
    </span>
  );
}

// ─── Trend Icon ───────────────────────────────────────────────────────────────

function TrendIcon({ trend }: { trend: string }) {
  if (trend === 'up') return <TrendingUp size={13} className="text-teal-500" />;
  if (trend === 'down') return <TrendingDown size={13} className="text-red-500" />;
  return <Minus size={13} className="text-gray-400" />;
}

// ─── Heatmap Cell ─────────────────────────────────────────────────────────────

function HeatCell({ value, tier, onClick }: { value: number; tier: 'critical' | 'high' | 'moderate' | 'low'; onClick: () => void }) {
  const base: Record<string, string> = {
    critical: value === 0 ? 'bg-red-50 text-red-300 dark:bg-red-950/20 dark:text-red-800' : value >= 3 ? 'bg-red-600 text-white' : value >= 2 ? 'bg-red-400 text-white' : 'bg-red-200 text-red-800 dark:bg-red-900 dark:text-red-300',
    high: value === 0 ? 'bg-orange-50 text-orange-300 dark:bg-orange-950/20 dark:text-orange-800' : value >= 4 ? 'bg-orange-500 text-white' : value >= 2 ? 'bg-orange-300 text-orange-900' : 'bg-orange-100 text-orange-700',
    moderate: value === 0 ? 'bg-amber-50 text-amber-300' : value >= 5 ? 'bg-amber-400 text-white' : value >= 3 ? 'bg-amber-200 text-amber-800' : 'bg-amber-100 text-amber-700',
    low: value === 0 ? 'bg-gray-50 text-gray-300' : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400',
  };
  const isClickable = value > 0;
  return (
    <td
      className={`text-center text-xs font-semibold py-2 px-3 rounded ${base[tier]} ${isClickable ? 'cursor-pointer hover:opacity-80 hover:ring-2 hover:ring-offset-1 hover:ring-current transition-all' : ''}`}
      onClick={isClickable ? onClick : undefined}
      title={isClickable ? `Filter vendors: ${tier} risk in this department` : undefined}
    >
      {value}
    </td>
  );
}

// ─── Custom Dot for clickable trend line ──────────────────────────────────────

function ClickableDot(props: { cx?: number; cy?: number; payload?: { month: string }; dataKey?: string; onDotClick: (severity: string, month: string) => void }) {
  const { cx, cy, payload, dataKey, onDotClick } = props;
  if (cx === undefined || cy === undefined || !payload || !dataKey) return null;
  const severityMap: Record<string, string> = { critical: 'Critical', high: 'High', moderate: 'Moderate', low: 'Low' };
  const severity = severityMap[dataKey] ?? dataKey;
  const colorMap: Record<string, string> = { critical: '#dc2626', high: '#f97316', moderate: '#d97706', low: '#6b7280' };
  return (
    <circle
      cx={cx}
      cy={cy}
      r={5}
      fill={colorMap[dataKey] ?? '#6b7280'}
      stroke="white"
      strokeWidth={1.5}
      style={{ cursor: 'pointer' }}
      onClick={() => onDotClick(severity, payload.month)}
    />
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function AnalyticsDashboard() {
  const { can } = useRole();
  const router = useRouter();
  const [activeTab, setActiveTab] = useState<'overview' | 'sla'>('overview');

  const totalCompliance = COMPLIANCE_STATUS_DATA.reduce((s, d) => s + d.value, 0);
  const compliantPct = Math.round((COMPLIANCE_STATUS_DATA[0].value / totalCompliance) * 100);
  const slaMetCount = SLA_METRICS.filter((m) => m.status === 'Met').length;
  const slaBreachCount = SLA_METRICS.filter((m) => m.status === 'Breached').length;
  const openIssues = REMEDIATION_FUNNEL_DATA[0].value - REMEDIATION_FUNNEL_DATA[4].value;

  // ─── Navigation handlers ───────────────────────────────────────────────────

  function handleHeatmapCellClick(dept: string, tier: string) {
    const params = new URLSearchParams({ risk: tier, dept });
    router.push(`/vendor-management?${params.toString()}`);
  }

  function handleTrendDotClick(severity: string, month: string) {
    const params = new URLSearchParams({ severity, month });
    router.push(`/issues?${params.toString()}`);
  }

  function handleFunnelStageClick(stage: typeof REMEDIATION_FUNNEL_DATA[0]) {
    if (stage.issueStatus) {
      const params = new URLSearchParams({ status: stage.issueStatus });
      router.push(`/issues?${params.toString()}`);
    } else {
      router.push('/issues');
    }
  }

  function handleExportPDF() {
    const complianceRows = COMPLIANCE_STATUS_DATA.map(d =>
      `<tr><td style="padding:6px 8px;border-bottom:1px solid #e5e7eb">${d.name}</td><td style="padding:6px 8px;border-bottom:1px solid #e5e7eb;font-weight:600">${d.value}</td><td style="padding:6px 8px;border-bottom:1px solid #e5e7eb">${Math.round((d.value/totalCompliance)*100)}%</td></tr>`
    ).join('');
    const slaRows = SLA_METRICS.map(m =>
      `<tr><td style="padding:6px 8px;border-bottom:1px solid #e5e7eb">${m.vendor}</td><td style="padding:6px 8px;border-bottom:1px solid #e5e7eb">${m.metric}</td><td style="padding:6px 8px;border-bottom:1px solid #e5e7eb">${m.target}</td><td style="padding:6px 8px;border-bottom:1px solid #e5e7eb;font-weight:600">${m.current}</td><td style="padding:6px 8px;border-bottom:1px solid #e5e7eb;font-weight:600;color:${m.status==='Met'?'#0d9488':m.status==='Breached'?'#dc2626':'#d97706'}">${m.status}</td></tr>`
    ).join('');
    const heatmapRows = VENDOR_RISK_HEATMAP.map(row =>
      `<tr><td style="padding:6px 8px;border-bottom:1px solid #e5e7eb;font-weight:600">${row.dept}</td><td style="padding:6px 8px;border-bottom:1px solid #e5e7eb;color:#dc2626;font-weight:600">${row.critical}</td><td style="padding:6px 8px;border-bottom:1px solid #e5e7eb;color:#f97316;font-weight:600">${row.high}</td><td style="padding:6px 8px;border-bottom:1px solid #e5e7eb;color:#d97706;font-weight:600">${row.moderate}</td><td style="padding:6px 8px;border-bottom:1px solid #e5e7eb;color:#6b7280;font-weight:600">${row.low}</td><td style="padding:6px 8px;border-bottom:1px solid #e5e7eb;font-weight:700">${row.critical+row.high+row.moderate+row.low}</td></tr>`
    ).join('');
    const funnelRows = REMEDIATION_FUNNEL_DATA.map(s =>
      `<tr><td style="padding:6px 8px;border-bottom:1px solid #e5e7eb">${s.name}</td><td style="padding:6px 8px;border-bottom:1px solid #e5e7eb;font-weight:600">${s.value}</td><td style="padding:6px 8px;border-bottom:1px solid #e5e7eb">${Math.round((s.value/REMEDIATION_FUNNEL_DATA[0].value)*100)}%</td></tr>`
    ).join('');

    const html = `<!DOCTYPE html><html><head><title>Analytics & Compliance – Summary Report</title>
    <style>
      body{font-family:system-ui,sans-serif;color:#111827;margin:0;padding:32px;font-size:13px}
      h1{font-size:20px;font-weight:700;margin:0 0 4px}
      h2{font-size:14px;font-weight:600;margin:24px 0 8px;color:#374151;border-bottom:2px solid #e5e7eb;padding-bottom:4px}
      .meta{color:#6b7280;font-size:12px;margin-bottom:24px}
      .kpi-grid{display:grid;grid-template-columns:repeat(4,1fr);gap:12px;margin-bottom:24px}
      .kpi{background:#f9fafb;border:1px solid #e5e7eb;border-radius:8px;padding:12px}
      .kpi-label{font-size:11px;color:#6b7280;text-transform:uppercase;letter-spacing:.05em;margin-bottom:4px}
      .kpi-value{font-size:20px;font-weight:700;color:#111827}
      table{width:100%;border-collapse:collapse;font-size:12px;margin-bottom:8px}
      th{text-align:left;padding:8px;background:#f3f4f6;font-weight:600;color:#374151;font-size:11px;text-transform:uppercase;letter-spacing:.05em}
      .footer{margin-top:32px;padding-top:16px;border-top:1px solid #e5e7eb;font-size:11px;color:#9ca3af;display:flex;justify-content:space-between}
      @media print{body{padding:16px}}
    </style></head><body>
    <h1>Analytics & Compliance Summary</h1>
    <div class="meta">City of Pleasanton TPRM · Risk trends, compliance status, SLA performance · Mar 2026</div>
    <div class="kpi-grid">
      <div class="kpi"><div class="kpi-label">Compliance Rate</div><div class="kpi-value" style="color:#0d9488">${compliantPct}%</div></div>
      <div class="kpi"><div class="kpi-label">Open Issues</div><div class="kpi-value" style="color:#dc2626">${openIssues}</div></div>
      <div class="kpi"><div class="kpi-label">SLA Met</div><div class="kpi-value" style="color:#2563eb">${slaMetCount}/${SLA_METRICS.length}</div></div>
      <div class="kpi"><div class="kpi-label">SLA Breaches</div><div class="kpi-value" style="color:${slaBreachCount>0?'#dc2626':'#0d9488'}">${slaBreachCount}</div></div>
    </div>
    <h2>Compliance Status Breakdown</h2>
    <table><thead><tr><th>Status</th><th>Vendors</th><th>Percentage</th></tr></thead><tbody>${complianceRows}</tbody></table>
    <h2>Remediation Funnel</h2>
    <table><thead><tr><th>Stage</th><th>Count</th><th>Percentage</th></tr></thead><tbody>${funnelRows}</tbody></table>
    <h2>Vendor Risk Heatmap by Department</h2>
    <table><thead><tr><th>Department</th><th style="color:#dc2626">Critical</th><th style="color:#f97316">High</th><th style="color:#d97706">Moderate</th><th style="color:#6b7280">Low</th><th>Total</th></tr></thead><tbody>${heatmapRows}</tbody></table>
    <h2>SLA Performance Detail</h2>
    <table><thead><tr><th>Vendor</th><th>Metric</th><th>Target</th><th>Current</th><th>Status</th></tr></thead><tbody>${slaRows}</tbody></table>
    <div class="footer"><span>City of Pleasanton TPRM · Analytics Report</span><span>Generated ${new Date().toLocaleDateString('en-US',{year:'numeric',month:'long',day:'numeric'})}</span></div>
    </body></html>`;

    const win = window.open('', '_blank');
    if (win) {
      win.document.write(html);
      win.document.close();
      win.focus();
      setTimeout(() => { win.print(); }, 400);
    }
  }

  return (
    <div className="flex flex-col h-full bg-[hsl(var(--background))]">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-[hsl(var(--border))] bg-[hsl(var(--surface))] flex-shrink-0">
        <div className="flex items-center gap-3">
          <Link
            href="/executive-dashboard"
            className="flex items-center gap-1.5 text-xs font-medium text-[hsl(var(--text-muted))] hover:text-[hsl(var(--text-primary))] transition-colors px-2 py-1.5 rounded-md hover:bg-[hsl(var(--background))] border border-transparent hover:border-[hsl(var(--border))]"
          >
            <ArrowLeft size={13} />
            Home
          </Link>
          <div>
            <h1 className="text-lg font-bold text-[hsl(var(--text-primary))]">Analytics & Compliance</h1>
            <p className="text-xs text-[hsl(var(--text-muted))] mt-0.5">Risk trends, compliance status, SLA performance · Mar 2026</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex rounded-lg border border-[hsl(var(--border))] overflow-hidden text-xs">
            <button
              onClick={() => setActiveTab('overview')}
              className={`px-3 py-1.5 font-medium transition-colors ${activeTab === 'overview' ? 'bg-[hsl(var(--primary))] text-white' : 'bg-[hsl(var(--surface))] text-[hsl(var(--text-muted))] hover:text-[hsl(var(--text-primary))]'}`}
            >
              Overview
            </button>
            <button
              onClick={() => setActiveTab('sla')}
              className={`px-3 py-1.5 font-medium transition-colors ${activeTab === 'sla' ? 'bg-[hsl(var(--primary))] text-white' : 'bg-[hsl(var(--surface))] text-[hsl(var(--text-muted))] hover:text-[hsl(var(--text-primary))]'}`}
            >
              SLA Performance
            </button>
          </div>
          {can('dashboard:export') && (
            <button
              onClick={handleExportPDF}
              className="btn-secondary text-xs flex items-center gap-1.5"
            >
              <Download size={13} /> Export PDF
            </button>
          )}
        </div>
      </div>

      {/* KPI Strip */}
      <div className="grid grid-cols-4 gap-3 px-6 py-4 flex-shrink-0">
        <KPICard label="Compliance Rate" value={`${compliantPct}%`} sub={`${COMPLIANCE_STATUS_DATA[0].value} of ${totalCompliance} vendors`} color="bg-teal-600" icon={ShieldCheck} />
        <KPICard label="Open Issues" value={openIssues} sub="Pending remediation" color="bg-red-600" icon={AlertTriangle} />
        <KPICard label="SLA Met" value={`${slaMetCount}/${SLA_METRICS.length}`} sub="Across active contracts" color="bg-blue-600" icon={CheckCircle2} />
        <KPICard label="SLA Breaches" value={slaBreachCount} sub="Require immediate action" color={slaBreachCount > 0 ? 'bg-red-700' : 'bg-teal-600'} icon={Clock} />
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto px-6 pb-6 space-y-4">
        {activeTab === 'overview' && (
          <>
            {/* Row 1: Compliance Pie + Risk Trend */}
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
              {/* Compliance Status Pie */}
              <div className="lg:col-span-2 card p-4">
                <div className="mb-3">
                  <h2 className="text-sm font-semibold text-[hsl(var(--text-primary))]">Compliance Status</h2>
                  <p className="text-xs text-[hsl(var(--text-muted))]">Vendor compliance breakdown · {totalCompliance} total</p>
                </div>
                <div className="flex items-center gap-4">
                  <ResponsiveContainer width="100%" height={180}>
                    <PieChart>
                      <Pie
                        data={COMPLIANCE_STATUS_DATA}
                        cx="50%"
                        cy="50%"
                        innerRadius={50}
                        outerRadius={80}
                        paddingAngle={2}
                        dataKey="value"
                      >
                        {COMPLIANCE_STATUS_DATA.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip
                        content={({ active, payload }) => {
                          if (!active || !payload?.length) return null;
                          const d = payload[0].payload;
                          return (
                            <div className="card p-2.5 shadow-md text-xs">
                              <div className="flex items-center gap-1.5 mb-1">
                                <span className="w-2.5 h-2.5 rounded-full" style={{ background: d.color }} />
                                <span className="font-semibold text-[hsl(var(--text-primary))]">{d.name}</span>
                              </div>
                              <p className="text-[hsl(var(--text-muted))]">{d.value} vendors ({Math.round((d.value / totalCompliance) * 100)}%)</p>
                            </div>
                          );
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="space-y-1.5 mt-1">
                  {COMPLIANCE_STATUS_DATA.map((d) => (
                    <div key={d.name} className="flex items-center justify-between text-xs">
                      <span className="flex items-center gap-1.5 text-[hsl(var(--text-muted))]">
                        <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: d.color }} />
                        {d.name}
                      </span>
                      <span className="font-semibold text-[hsl(var(--text-primary))] tabular-nums">
                        {d.value} <span className="font-normal text-[hsl(var(--text-muted))]">({Math.round((d.value / totalCompliance) * 100)}%)</span>
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Risk Trend Line Chart */}
              <div className="lg:col-span-3 card p-4">
                <div className="mb-3">
                  <h2 className="text-sm font-semibold text-[hsl(var(--text-primary))]">Risk Trend by Severity</h2>
                  <p className="text-xs text-[hsl(var(--text-muted))]">Click a data point to filter Issues by severity · Oct 2025 – Mar 2026</p>
                </div>
                <ResponsiveContainer width="100%" height={220}>
                  <LineChart data={RISK_TREND_DATA} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(220 13% 88%)" vertical={false} />
                    <XAxis dataKey="month" tick={{ fontSize: 11, fill: 'hsl(220 8% 60%)' }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 11, fill: 'hsl(220 8% 60%)' }} axisLine={false} tickLine={false} />
                    <Tooltip content={<TooltipCard />} />
                    <Legend wrapperStyle={{ fontSize: '11px', paddingTop: '8px' }} iconType="circle" iconSize={8} />
                    <Line type="monotone" dataKey="critical" name="Critical" stroke="#dc2626" strokeWidth={2} dot={(props) => { const { key, ...rest } = props as typeof props & { key?: React.Key }; return <ClickableDot {...rest} dataKey="critical" onDotClick={handleTrendDotClick} />; }} activeDot={{ r: 6, cursor: 'pointer' }} />
                    <Line type="monotone" dataKey="high" name="High" stroke="#f97316" strokeWidth={2} dot={(props) => { const { key, ...rest } = props as typeof props & { key?: React.Key }; return <ClickableDot {...rest} dataKey="high" onDotClick={handleTrendDotClick} />; }} activeDot={{ r: 6, cursor: 'pointer' }} />
                    <Line type="monotone" dataKey="moderate" name="Moderate" stroke="#d97706" strokeWidth={2} dot={(props) => { const { key, ...rest } = props as typeof props & { key?: React.Key }; return <ClickableDot {...rest} dataKey="moderate" onDotClick={handleTrendDotClick} />; }} activeDot={{ r: 6, cursor: 'pointer' }} />
                    <Line type="monotone" dataKey="low" name="Low" stroke="#6b7280" strokeWidth={1.5} strokeDasharray="4 2" dot={(props) => { const { key, ...rest } = props as typeof props & { key?: React.Key }; return <ClickableDot {...rest} dataKey="low" onDotClick={handleTrendDotClick} />; }} activeDot={{ r: 6, cursor: 'pointer' }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Row 2: Remediation Funnel */}
            <div className="card p-4">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-sm font-semibold text-[hsl(var(--text-primary))]">Remediation Funnel</h2>
                  <p className="text-xs text-[hsl(var(--text-muted))]">Click a stage to filter Issues by status — current snapshot</p>
                </div>
                <span className="text-xs text-[hsl(var(--text-muted))]">
                  Closure rate: <span className="font-semibold text-teal-600">{Math.round((REMEDIATION_FUNNEL_DATA[4].value / REMEDIATION_FUNNEL_DATA[0].value) * 100)}%</span>
                </span>
              </div>
              <div className="flex items-end justify-between gap-3">
                {REMEDIATION_FUNNEL_DATA.map((stage) => {
                  const pct = Math.round((stage.value / REMEDIATION_FUNNEL_DATA[0].value) * 100);
                  const maxH = 140;
                  const barH = Math.max(24, Math.round((stage.value / REMEDIATION_FUNNEL_DATA[0].value) * maxH));
                  return (
                    <div
                      key={stage.name}
                      className="flex-1 flex flex-col items-center gap-2 cursor-pointer group"
                      onClick={() => handleFunnelStageClick(stage)}
                      title={`Filter Issues: ${stage.name}`}
                    >
                      <span className="text-sm font-bold text-[hsl(var(--text-primary))] tabular-nums">{stage.value}</span>
                      <div
                        className="w-full rounded-t-md transition-all group-hover:opacity-75 group-hover:scale-y-105 origin-bottom"
                        style={{ height: `${barH}px`, background: stage.fill }}
                      />
                      <div className="text-center">
                        <p className="text-2xs font-medium text-[hsl(var(--text-primary))] leading-tight group-hover:text-[hsl(var(--accent))] transition-colors">{stage.name}</p>
                        <p className="text-2xs text-[hsl(var(--text-muted))]">{pct}%</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Row 3: Vendor Risk Heatmap */}
            <div className="card p-4">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-sm font-semibold text-[hsl(var(--text-primary))]">Vendor Risk Heatmap by Department</h2>
                  <p className="text-xs text-[hsl(var(--text-muted))]">Click a cell to filter Vendor Management by department & risk tier</p>
                </div>
                <div className="flex items-center gap-3 text-2xs text-[hsl(var(--text-muted))]">
                  <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-red-600 inline-block" /> Critical</span>
                  <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-orange-400 inline-block" /> High</span>
                  <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-amber-300 inline-block" /> Moderate</span>
                  <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-gray-200 inline-block" /> Low</span>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-[hsl(var(--border))]">
                      <th className="text-left py-2 pr-4 font-semibold text-[hsl(var(--text-muted))] w-32">Department</th>
                      <th className="text-center py-2 px-3 font-semibold text-red-600">Critical</th>
                      <th className="text-center py-2 px-3 font-semibold text-orange-500">High</th>
                      <th className="text-center py-2 px-3 font-semibold text-amber-600">Moderate</th>
                      <th className="text-center py-2 px-3 font-semibold text-gray-500">Low</th>
                      <th className="text-center py-2 px-3 font-semibold text-[hsl(var(--text-muted))]">Total</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[hsl(var(--border))]">
                    {VENDOR_RISK_HEATMAP.map((row) => {
                      const total = row.critical + row.high + row.moderate + row.low;
                      return (
                        <tr key={row.dept} className="hover:bg-[hsl(var(--surface-hover,var(--surface)))]">
                          <td className="py-2 pr-4 font-medium text-[hsl(var(--text-primary))]">{row.dept}</td>
                          <HeatCell value={row.critical} tier="critical" onClick={() => handleHeatmapCellClick(row.dept, 'Critical')} />
                          <HeatCell value={row.high} tier="high" onClick={() => handleHeatmapCellClick(row.dept, 'High')} />
                          <HeatCell value={row.moderate} tier="moderate" onClick={() => handleHeatmapCellClick(row.dept, 'Moderate')} />
                          <HeatCell value={row.low} tier="low" onClick={() => handleHeatmapCellClick(row.dept, 'Low')} />
                          <td className="text-center py-2 px-3 font-semibold text-[hsl(var(--text-primary))]">{total}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 border-[hsl(var(--border))] bg-[hsl(var(--surface))]">
                      <td className="py-2 pr-4 font-bold text-[hsl(var(--text-primary))] text-xs">Total</td>
                      <td className="text-center py-2 px-3 font-bold text-red-600">{VENDOR_RISK_HEATMAP.reduce((s, r) => s + r.critical, 0)}</td>
                      <td className="text-center py-2 px-3 font-bold text-orange-500">{VENDOR_RISK_HEATMAP.reduce((s, r) => s + r.high, 0)}</td>
                      <td className="text-center py-2 px-3 font-bold text-amber-600">{VENDOR_RISK_HEATMAP.reduce((s, r) => s + r.moderate, 0)}</td>
                      <td className="text-center py-2 px-3 font-bold text-gray-500">{VENDOR_RISK_HEATMAP.reduce((s, r) => s + r.low, 0)}</td>
                      <td className="text-center py-2 px-3 font-bold text-[hsl(var(--text-primary))]">{VENDOR_RISK_HEATMAP.reduce((s, r) => s + r.critical + r.high + r.moderate + r.low, 0)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </>
        )}

        {activeTab === 'sla' && (
          <>
            {/* SLA Trend Chart */}
            <div className="card p-4">
              <div className="mb-3">
                <h2 className="text-sm font-semibold text-[hsl(var(--text-primary))]">SLA Performance Trend</h2>
                <p className="text-xs text-[hsl(var(--text-muted))]">Monthly SLA compliance across all active contracts · Oct 2025 – Mar 2026</p>
              </div>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={SLA_SUMMARY} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(220 13% 88%)" vertical={false} />
                  <XAxis dataKey="month" tick={{ fontSize: 11, fill: 'hsl(220 8% 60%)' }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: 'hsl(220 8% 60%)' }} axisLine={false} tickLine={false} unit="%" />
                  <Tooltip content={<TooltipCard />} />
                  <Legend wrapperStyle={{ fontSize: '11px', paddingTop: '8px' }} iconType="square" iconSize={8} />
                  <Bar dataKey="met" name="Met" stackId="a" fill="#0d9488" />
                  <Bar dataKey="atRisk" name="At Risk" stackId="a" fill="#d97706" />
                  <Bar dataKey="breached" name="Breached" stackId="a" fill="#dc2626" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* SLA Table */}
            <div className="card p-4">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-sm font-semibold text-[hsl(var(--text-primary))]">SLA Obligations Detail</h2>
                  <p className="text-xs text-[hsl(var(--text-muted))]">Current performance vs. contractual targets</p>
                </div>
                <div className="flex items-center gap-3 text-2xs">
                  <span className="flex items-center gap-1 text-teal-600"><span className="w-2 h-2 rounded-full bg-teal-500 inline-block" /> Met ({SLA_METRICS.filter(m => m.status === 'Met').length})</span>
                  <span className="flex items-center gap-1 text-amber-600"><span className="w-2 h-2 rounded-full bg-amber-500 inline-block" /> At Risk ({SLA_METRICS.filter(m => m.status === 'At Risk').length})</span>
                  <span className="flex items-center gap-1 text-red-600"><span className="w-2 h-2 rounded-full bg-red-500 inline-block" /> Breached ({SLA_METRICS.filter(m => m.status === 'Breached').length})</span>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-[hsl(var(--border))]">
                      <th className="text-left py-2 pr-4 font-semibold text-[hsl(var(--text-muted))]">Vendor</th>
                      <th className="text-left py-2 pr-4 font-semibold text-[hsl(var(--text-muted))]">Metric</th>
                      <th className="text-left py-2 pr-4 font-semibold text-[hsl(var(--text-muted))]">Target</th>
                      <th className="text-left py-2 pr-4 font-semibold text-[hsl(var(--text-muted))]">Current</th>
                      <th className="text-left py-2 pr-4 font-semibold text-[hsl(var(--text-muted))]">Status</th>
                      <th className="text-center py-2 font-semibold text-[hsl(var(--text-muted))]">Trend</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[hsl(var(--border))]">
                    {SLA_METRICS.map((m, i) => (
                      <tr key={i} className="hover:bg-[hsl(var(--surface))]">
                        <td className="py-2.5 pr-4 font-medium text-[hsl(var(--text-primary))]">{m.vendor}</td>
                        <td className="py-2.5 pr-4 text-[hsl(var(--text-muted))]">{m.metric}</td>
                        <td className="py-2.5 pr-4 font-mono text-[hsl(var(--text-primary))]">{m.target}</td>
                        <td className={`py-2.5 pr-4 font-mono font-semibold ${m.status === 'Met' ? 'text-teal-600' : m.status === 'Breached' ? 'text-red-600' : 'text-amber-600'}`}>{m.current}</td>
                        <td className="py-2.5 pr-4"><SLABadge status={m.status} /></td>
                        <td className="py-2.5 text-center"><TrendIcon trend={m.trend} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
